import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { planReview, REVIEW_REASON_MAX } from '../../lib/portalAssets/review';
import type { PortalAssetTile } from '../../types/portalAsset';

// Approve, or not approve with a reason. ⚠️ SHARED SINCE 2026-10-08 (it was
// staff-only): a club and Kyck are equal authorities, either can set either
// status and overturn the other. It names no endpoint; the host supplies onReview.
//
// ⚠️ The buttons are enabled by the SAME planReview the server runs, so a file
// still processing shows WHY it cannot be reviewed instead of a button that
// fails. The server still decides; a refusal it sends back is shown as is.
//
// ⚠️ The reason box says WHO READS IT, and that depends on who is writing: Kyck's
// reason is read by the club, the club's by Kyck. A reason written as an internal
// note ("dodgy lighting, ask Sam") reads very differently on the other side.

const READER: Record<'client' | 'staff', { note: string; placeholder: string }> = {
  staff: { note: 'The club sees this.', placeholder: 'e.g. Too dark to use in an ad — try one taken in daylight.' },
  client: { note: 'Kyck sees this.', placeholder: "e.g. This member hasn't agreed to be in ads." },
};

export function ReviewControls({
  tile,
  audience,
  onReview,
  pending,
  error,
}: {
  /** Who is reviewing — decides who the reason box says will read it. */
  audience: 'client' | 'staff';
  tile: Pick<PortalAssetTile, 'lifecycle' | 'review' | 'deletedDaysLeft'>;
  onReview: (input: { decision: 'approved' | 'rejected'; reason?: string }) => void;
  pending: boolean;
  error: string | null;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  // A tile carries `deletedDaysLeft` rather than the instant; non-null = deleted.
  const current = {
    lifecycle: tile.lifecycle,
    deletedAt: tile.deletedDaysLeft !== null ? 'deleted' : null,
    review: { ...tile.review, by: null },
  };
  const approve = planReview(current, { decision: 'approved' });
  const blocked = approve.kind === 'refuse' && approve.status === 409 ? approve.message : null;

  if (blocked) return <p className="text-sm text-muted-foreground">{blocked}</p>;

  // Approved is approved, whoever decided: an already-approved asset offers no re-approve.
  const approveLabel = tile.review.status === 'approved' ? 'Approved' : 'Approve';

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <p className="text-sm font-medium text-foreground">Review</p>
      {!rejecting ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={pending || approve.kind === 'noop'}
            onClick={() => onReview({ decision: 'approved' })}
          >
            {approveLabel}
          </Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setRejecting(true)}>
            Don't approve…
          </Button>
        </div>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            onReview({ decision: 'rejected', reason });
          }}
        >
          <label htmlFor="asset-reject-reason" className="block text-sm text-foreground">
            Why not? <span className="text-muted-foreground">{READER[audience].note}</span>
          </label>
          <Textarea
            id="asset-reject-reason"
            value={reason}
            maxLength={REVIEW_REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            placeholder={READER[audience].placeholder}
          />
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={pending || reason.trim() === ''}>
              Don't approve
            </Button>
            <Button size="sm" type="button" variant="ghost" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
